/* global removeTrapFocus, theme */

const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    this.searchInput = this.querySelector('.main-search__input');
    this.resultsBox = this.querySelector('.main-search__results');
    this.clearButton = this.querySelector('.bvt-search-clear');
    this.status = this.querySelector('#HeaderSearchResultsStatus');
    this.activeResultIndex = -1;
    this.searchTimeoutID = -1;
    this.searchAbortController = null;
    this.oldSearchValue = '';

    this.querySelectorAll('.main-search__close').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.cancelPendingSearch();
        document.body.classList.remove('show-search');
        removeTrapFocus(theme.trapOpener);
      });
    });

    this.clearButton?.addEventListener('click', () => {
      this.searchInput.value = '';
      this.setBlankState();
      this.searchInput.focus();
    });

    this.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.addEventListener('focusin', () => {
      if (document.body.classList.contains('show-search')) {
        window.setTimeout(() => removeTrapFocus(), 0);
      }
    });

    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }
  }

  get options() {
    return [...this.querySelectorAll('.main-search__results [role="option"]')]
      .filter((option) => !option.hidden && option.getClientRects().length);
  }

  resetActiveOption() {
    this.activeResultIndex = -1;
    this.searchInput.setAttribute('aria-activedescendant', '');
    this.options.forEach((option) => {
      option.classList.remove('search-result-active');
      option.setAttribute('aria-selected', 'false');
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      const closeButton = this.querySelector('.main-search__close');
      if (closeButton) {
        event.preventDefault();
        closeButton.click();
      }
      return;
    }

    if (event.key === 'Tab') {
      const focusables = [...this.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      if (!focusables.length) {
        event.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        event.stopPropagation();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        event.stopPropagation();
        first.focus();
      }
      return;
    }

    if (event.target !== this.searchInput) return;

    const options = this.options;
    if (!options.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (event.key === 'ArrowDown') {
        this.activeResultIndex = (this.activeResultIndex + 1) % options.length;
      } else {
        this.activeResultIndex = (this.activeResultIndex - 1 + options.length) % options.length;
      }

      options.forEach((option, index) => {
        const active = index === this.activeResultIndex;
        option.classList.toggle('search-result-active', active);
        option.setAttribute('aria-selected', String(active));
      });
      this.searchInput.setAttribute('aria-activedescendant', options[this.activeResultIndex].id);
    }

    if (event.key === 'Enter' && this.activeResultIndex >= 0) {
      const active = options[this.activeResultIndex];
      const destination = active.matches('a') ? active : active.querySelector('a');
      if (destination) {
        event.preventDefault();
        destination.click();
      }
    }
  }

  cancelPendingSearch() {
    window.clearTimeout(this.searchTimeoutID);
    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }
  }

  setBlankState() {
    this.cancelPendingSearch();
    this.oldSearchValue = '';
    this.resultsBox.innerHTML = '';
    this.classList.remove('main-search--loading', 'main-search--has-results', 'main-search--no-results', 'main-search--short-query');
    this.classList.add('main-search--blank');
    this.searchInput.setAttribute('aria-expanded', 'false');
    this.clearButton.hidden = true;
    this.resetActiveOption();
    if (this.status) this.status.textContent = 'Search Burvonti';
  }

  setShortQueryState() {
    this.cancelPendingSearch();
    this.resultsBox.innerHTML = '<p class="bvt-search-short-hint" role="status">Type one more character to see suggestions.</p>';
    this.classList.remove('main-search--loading', 'main-search--has-results', 'main-search--no-results', 'main-search--blank');
    this.classList.add('main-search--short-query');
    this.searchInput.setAttribute('aria-expanded', 'false');
    this.resetActiveOption();
    if (this.status) this.status.textContent = 'Type one more character';
  }

  initQuickSearch() {
    const searchTimeoutThrottle = 320;
    const includeMeta = this.dataset.quickSearchMeta === 'true';

    const handleInputChange = () => {
      const valueToSearch = this.searchInput.value.trim();
      this.clearButton.hidden = valueToSearch.length === 0;

      if (!valueToSearch.length) {
        this.setBlankState();
        return;
      }

      if (valueToSearch.length === 1) {
        this.oldSearchValue = valueToSearch;
        this.setShortQueryState();
        return;
      }

      if (valueToSearch === this.oldSearchValue) return;
      this.oldSearchValue = valueToSearch;
      this.cancelPendingSearch();
      this.resetActiveOption();

      const form = this.searchInput.closest('form');
      const linkURL = new URL(form.action);
      const formParams = new URLSearchParams(new FormData(form));
      formParams.forEach((value, key) => linkURL.searchParams.set(key, value));

      this.classList.remove('main-search--has-results', 'main-search--no-results', 'main-search--blank', 'main-search--short-query');
      this.classList.add('main-search--loading');
      this.resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
      this.searchInput.setAttribute('aria-expanded', 'false');
      if (this.status) this.status.textContent = `Searching for ${valueToSearch}`;

      this.searchTimeoutID = window.setTimeout(() => {
        this.searchAbortController = new AbortController();
        const ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
        ajaxUrl.searchParams.set('q', valueToSearch);
        ajaxUrl.searchParams.set('section_id', 'predictive-search');
        ajaxUrl.searchParams.set('resources[type]', 'product,collection,page,article,query');
        ajaxUrl.searchParams.set('resources[limit]', '5');
        ajaxUrl.searchParams.set('resources[limit_scope]', 'each');
        ajaxUrl.searchParams.set(
          'resources[options][fields]',
          includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor'
        );

        fetch(ajaxUrl, { method: 'get', signal: this.searchAbortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            return response.text();
          })
          .then((response) => {
            if (this.searchInput.value.trim() !== valueToSearch) return;
            const template = document.createElement('template');
            template.innerHTML = response;
            const results = template.content.querySelector('#predictive-search-results');
            const resultsMessage = template.content.querySelector('[data-results-message]');

            this.searchAbortController = null;
            this.classList.remove('main-search--loading', 'main-search--no-results');
            this.resultsBox.innerHTML = '';

            if (!results || !results.querySelector('[role="option"]')) {
              this.classList.add('main-search--no-results');
              this.resultsBox.innerHTML = '<div class="main-search__empty-message">No direct result. Try a broader term or view all search results.</div>';
              this.searchInput.setAttribute('aria-expanded', 'false');
              if (this.status) this.status.textContent = `No predictive results for ${valueToSearch}`;
              return;
            }

            this.resultsBox.appendChild(results);
            const allLink = document.createElement('a');
            allLink.className = 'main-search__results-all-link bvt-search-view-all';
            allLink.href = linkURL;
            allLink.textContent = 'View all product results';
            this.resultsBox.appendChild(allLink);
            this.classList.add('main-search--has-results');
            this.searchInput.setAttribute('aria-expanded', 'true');
            this.resetActiveOption();
            if (this.status) {
              this.status.innerHTML = resultsMessage?.innerHTML || `Predictive results for ${valueToSearch}`;
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.warn(error); // eslint-disable-line no-console
            this.classList.remove('main-search--loading');
            this.classList.add('main-search--no-results');
          });
      }, searchTimeoutThrottle);
    };

    this.searchInput.addEventListener('input', handleInputChange);
    this.searchInput.addEventListener('change', handleInputChange);
    this.setBlankState();
  }
};

if (!window.customElements.get('main-search')) {
  window.customElements.define('main-search', MainSearch);
}

const preserveProductOnlySearchState = () => {
  const searchPage = document.querySelector('.bvt-search-results-section');
  if (!searchPage) return;

  const exactCollectionIntent = searchPage.querySelector('.bvt-search-results--exact-collection-intent');
  if (exactCollectionIntent) {
    exactCollectionIntent.querySelector('#FilterGroupSort')?.closest('.filter-group')?.setAttribute('hidden', '');
    const currentUrl = new URL(window.location.href);
    const currentSort = currentUrl.searchParams.get('sort_by');
    if (currentSort && currentSort !== 'relevance') {
      currentUrl.searchParams.set('sort_by', 'relevance');
      currentUrl.searchParams.set('type', 'product');
      window.location.replace(currentUrl.toString());
      return;
    }
  }

  const filterForm = searchPage.querySelector('#CollectionFilterForm');
  if (filterForm && !filterForm.querySelector('input[name="type"]')) {
    const typeInput = document.createElement('input');
    typeInput.type = 'hidden';
    typeInput.name = 'type';
    typeInput.value = 'product';
    filterForm.appendChild(typeInput);
  }

  const searchPath = new URL(searchPage.querySelector('.bvt-search-page-form form')?.action || '/search', window.location.origin).pathname;
  searchPage.querySelectorAll('.link-dropdown__options a[href], .bvt-search-pagination a[href], .active-filter-controls a[href]').forEach((link) => {
    const url = new URL(link.href, window.location.origin);
    if (url.pathname === searchPath) {
      url.searchParams.set('type', 'product');
      link.href = url.toString();
    }
  });
};

preserveProductOnlySearchState();

const searchPageRoot = document.querySelector('.bvt-search-results-section');
if (searchPageRoot) {
  new MutationObserver(preserveProductOnlySearchState).observe(searchPageRoot, {
    childList: true,
    subtree: true,
  });

  let searchFilterOpener = null;
  const searchFilterContainer = searchPageRoot.querySelector('filter-container');

  const closeSearchFilter = () => {
    const close = searchFilterContainer?.querySelector('.filters__close');
    if (close && searchFilterContainer.classList.contains('filter-container--show-filters-mobile')) {
      close.click();
    }
  };

  searchPageRoot.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-toggle-filters]');
    if (!toggle || !window.matchMedia('(max-width: 999.98px)').matches) return;

    window.setTimeout(() => {
      const isOpen = searchFilterContainer?.classList.contains('filter-container--show-filters-mobile');
      const panel = searchFilterContainer?.querySelector('.filters');
      if (isOpen) {
        if (toggle.matches('.toggle-btn')) searchFilterOpener = toggle;
        document.body.classList.add('bvt-search-filter-open');
        panel?.setAttribute('role', 'dialog');
        panel?.setAttribute('aria-modal', 'true');
        searchFilterContainer?.querySelector('.filters__close')?.focus();
      } else {
        document.body.classList.remove('bvt-search-filter-open');
        panel?.removeAttribute('role');
        panel?.removeAttribute('aria-modal');
        searchFilterOpener?.focus();
      }
    }, 0);
  });

  searchPageRoot.addEventListener('keydown', (event) => {
    if (!searchFilterContainer?.classList.contains('filter-container--show-filters-mobile')) return;
    const panel = searchFilterContainer.querySelector('.filters');
    if (!panel) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearchFilter();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusables = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    if (!focusables.length) {
      event.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}
